import apiClient from '../api/apiClient';

const BASE = '/api/v1/admin/investors/onboarding';

/** Unwrap common API envelope { success, data } */
function unwrap(res) {
  const body = res?.data;
  if (body && typeof body === 'object' && 'data' in body) return body.data;
  return body;
}

const investorOnboardingService = {
  /**
   * GET /api/v1/admin/partners — Partner picker for investor onboarding.
   * Response: { data: { users: [...], pagination, statistics } }
   */
  listIFAs: async (params = {}) => {
    const defaultParams = {
      status: 'active',
      is_active: '',
      search: '',
      role: '',
      manager_id: '',
      role_id: '',
      page: 1,
      limit: 100,
      ...params,
    };
    const response = await apiClient.get('/api/v1/admin/partners', { params: defaultParams });
    const body = response.data;
    const inner = body?.data !== undefined ? body.data : body;
    if (Array.isArray(inner?.users)) return inner.users;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(inner?.items)) return inner.items;
    if (Array.isArray(inner?.partners)) return inner.partners;
    if (Array.isArray(inner?.data)) return inner.data;
    return [];
  },

  // ─── INVESTOR ONBOARDING V2 (Global / Shared Pool API) ─────────────
  
  startMobile: async (payload) => {
    // { mobile, ifa_id, portal?: 'admin' }
    const response = await apiClient.post('/api/v1/onboarding/start-mobile', payload);
    return unwrap(response);
  },

  checkPan: async (payload) => {
    const { pan, ifa_id, session_id, mobile } = payload;
    const response = await apiClient.get('/api/v1/onboarding/check-pan', {
      params: {
        pan,
        ifa_id,
        session_id,
        mobile,
      },
    });
    return unwrap(response);
  },

  savePersonalDetails: async (payload) => {
    // { session_id, pan, name, mobile, email, dob, gender, investor_type, is_minor... }
    const response = await apiClient.post('/api/v1/onboarding/save-personal-details', payload);
    return unwrap(response);
  },

  verifyBank: async (payload) => {
    // { investor_id, account_number, ifsc, bank_name, branch_name, beneficiary_name }
    const response = await apiClient.post('/api/v1/onboarding/verify-bank', payload);
    return unwrap(response);
  },

  uploadBankMismatchCheque: async (formData) => {
    const response = await apiClient.post(
      '/api/v1/admin/investors/onboarding/bank-mismatch-cheque',
      formData,
      {
        transformRequest: [
          (data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
              if (headers?.delete) headers.delete('Content-Type');
              else if (headers) {
                delete headers['Content-Type'];
                delete headers['content-type'];
              }
            }
            return data;
          },
        ],
      }
    );
    return unwrap(response);
  },

  selectBank: async (payload) => {
    // { session_id, bank_account_id }
    const response = await apiClient.post('/api/v1/onboarding/select-bank', payload);
    return unwrap(response);
  },

  uploadCml: async (formData) => {
    // multipart: const fd = new FormData(); fd.append('investor_id', id); ...
    const response = await apiClient.post('/api/v1/onboarding/upload-cml', formData, {
      transformRequest: [
        (data, headers) => {
          if (typeof FormData !== 'undefined' && data instanceof FormData) {
            if (headers?.delete) headers.delete('Content-Type');
            else if (headers) {
              delete headers['Content-Type'];
              delete headers['content-type'];
            }
          }
          return data;
        },
      ],
    });
    return unwrap(response);
  },

  selectDemat: async (payload) => {
    // { session_id, demat_account_id }
    const response = await apiClient.post('/api/v1/onboarding/select-demat', payload);
    return unwrap(response);
  },

  completeOnboarding: async (payload) => {
    // { session_id, bank_account_id?, demat_account_id?, holding_type? }
    const response = await apiClient.post('/api/v1/onboarding/complete', payload);
    return unwrap(response);
  },

  // ─── INVESTOR ONBOARDING V1 (Legacy, to be deleted) ────────────────

  start: async (ifaId) => {
    const response = await apiClient.post(`${BASE}/start`, { ifa_id: String(ifaId) });
    return unwrap(response);
  },

  checkInvestor: async (params) => {
    // params can be { pan: '...' } or { email: '...' }
    const response = await apiClient.get(`${BASE}/check-investor`, { params });
    return unwrap(response);
  },

  submitPersonal: async (payload) => {
    const response = await apiClient.post(`${BASE}/personal`, payload);
    return unwrap(response);
  },

  submitBank: async (payload) => {
    const response = await apiClient.post(`${BASE}/bank`, payload);
    return unwrap(response);
  },

  /**
   * Multipart upload — must not use JSON Content-Type (apiClient default).
   * Otherwise the body is wrong and the server sees no file (same as curl with file: {}).
   */
  submitDemat: async (formData) => {
    const response = await apiClient.post(`${BASE}/demat`, formData, {
      transformRequest: [
        (data, headers) => {
          if (typeof FormData !== 'undefined' && data instanceof FormData) {
            if (headers && typeof headers.delete === 'function') {
              headers.delete('Content-Type');
            } else if (headers) {
              delete headers['Content-Type'];
              delete headers['content-type'];
            }
          }
          return data;
        },
      ],
    });
    return unwrap(response);
  },

  getStatus: async (sessionUuid) => {
    // We update this specifically to hit the V2 endpoint because new incoming sessions use V2 status schema
    const response = await apiClient.get(`/api/v1/onboarding/status/${sessionUuid}`);
    return unwrap(response);
  },

  /**
   * DELETE /api/v1/admin/investor-onboarding/cancel/:session_uuid
   * Cancels an in-progress onboarding session on the server.
   */
  cancelSession: async (sessionUuid) => {
    const response = await apiClient.delete(
      `/api/v1/admin/investor-onboarding/cancel/${encodeURIComponent(sessionUuid)}`
    );
    return unwrap(response);
  },

  updateMobile: async (sessionUuid, mobile) => {
    const response = await apiClient.patch(`${BASE}/mobile/${sessionUuid}`, { mobile });
    return unwrap(response);
  },
};

export default investorOnboardingService;
